import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  TableSortLabel,
  Stack,
  TablePagination,
  Box,
} from "@mui/material";

import HelpOutline from "@mui/icons-material/HelpOutline";
import { PlanGenerationAttemptOut } from "@providers/hop-ord-server/api";
import dayjs from "dayjs";
import { useMemo, useState, useEffect, ChangeEvent } from "react";
import { toLocaleDateTimeString, formatDurationRange } from "@utils";

// use shared utils for formatting

export const COLOUR_BLIND_COLOURS = {
  inProgress: "#F9E20A", // Yellow
  success: "#0072B2", // Blue
  error: "#D55E00", // Red
} as const;

const COL_WIDTHS = {
  orderId: 90,
  planName: 320,
  template: 180,
  prescription: 280,
  started: 185,
  finished: 185,
  duration: 130,
  timeInQueue: 130,
  status: 120,
} as const;

interface MetricsTableProps {
  rows: PlanGenerationAttemptOut[];
}

const getStartedAtMs = (row: PlanGenerationAttemptOut): number | undefined => {
  return row.planningStartedAt
    ? dayjs(row.planningStartedAt).valueOf()
    : undefined;
};

const getFinishedAtMs = (row: PlanGenerationAttemptOut): number | undefined => {
  return row.finishedAt ? dayjs(row.finishedAt).valueOf() : undefined;
};

const getDurationSeconds = (
  row: PlanGenerationAttemptOut,
): number | undefined => {
  if (!row.planningStartedAt || !row.finishedAt) return undefined;
  const seconds = dayjs(row.finishedAt).diff(
    dayjs(row.planningStartedAt),
    "second",
  );
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  return seconds;
};

const getStatusRank = (row: PlanGenerationAttemptOut): number => {
  if (!row.finishedAt) return 0; // In Progress
  return row.success ? 2 : 1; // Success:2, Error:1
};

const MetricsTable = ({ rows }: MetricsTableProps) => {
  type SortKey = "started" | "finished" | "duration" | "status";
  const [sortKey, setSortKey] = useState<SortKey>("started");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const compareValues = (
    a?: number,
    b?: number,
    asc: boolean = true,
  ): number => {
    const aVal = a ?? Number.POSITIVE_INFINITY;
    const bVal = b ?? Number.POSITIVE_INFINITY;
    return asc ? aVal - bVal : bVal - aVal;
  };

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const asc = sortDirection === "asc";
      switch (sortKey) {
        case "started":
          return compareValues(getStartedAtMs(a), getStartedAtMs(b), asc);
        case "finished":
          return compareValues(getFinishedAtMs(a), getFinishedAtMs(b), asc);
        case "duration":
          return compareValues(
            getDurationSeconds(a),
            getDurationSeconds(b),
            asc,
          );
        case "status":
          return compareValues(getStatusRank(a), getStatusRank(b), asc);
        default:
          return 0;
      }
    });
    return copy;
  }, [rows, sortKey, sortDirection]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedRows.slice(start, end);
  }, [sortedRows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [sortKey, sortDirection, rows]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
  };

  const handleRequestSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table
        size="small"
        stickyHeader
        aria-label="Plan generation attempts"
        sx={{ tableLayout: "fixed" }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                width: COL_WIDTHS.orderId,
                minWidth: COL_WIDTHS.orderId,
                maxWidth: COL_WIDTHS.orderId,
              }}
            >
              Order ID
            </TableCell>
            <TableCell
              sx={{
                width: COL_WIDTHS.planName,
                minWidth: COL_WIDTHS.planName,
                maxWidth: COL_WIDTHS.planName,
              }}
            >
              Plan name
            </TableCell>
            <TableCell
              sx={{
                width: COL_WIDTHS.template,
                minWidth: COL_WIDTHS.template,
                maxWidth: COL_WIDTHS.template,
              }}
            >
              Template
            </TableCell>
            <TableCell
              sx={{
                width: COL_WIDTHS.prescription,
                minWidth: COL_WIDTHS.prescription,
                maxWidth: COL_WIDTHS.prescription,
              }}
            >
              Prescription
            </TableCell>
            <TableCell
              sortDirection={sortKey === "started" ? sortDirection : false}
              sx={{
                width: COL_WIDTHS.started,
                minWidth: COL_WIDTHS.started,
                maxWidth: COL_WIDTHS.started,
              }}
            >
              <TableSortLabel
                active={sortKey === "started"}
                direction={sortKey === "started" ? sortDirection : "asc"}
                onClick={() => handleRequestSort("started")}
              >
                Started
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={sortKey === "finished" ? sortDirection : false}
              sx={{
                width: COL_WIDTHS.finished,
                minWidth: COL_WIDTHS.finished,
                maxWidth: COL_WIDTHS.finished,
              }}
            >
              <TableSortLabel
                active={sortKey === "finished"}
                direction={sortKey === "finished" ? sortDirection : "asc"}
                onClick={() => handleRequestSort("finished")}
              >
                Finished
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={sortKey === "duration" ? sortDirection : false}
              sx={{
                width: COL_WIDTHS.duration,
                minWidth: COL_WIDTHS.duration,
                maxWidth: COL_WIDTHS.duration,
              }}
            >
              <TableSortLabel
                active={sortKey === "duration"}
                direction={sortKey === "duration" ? sortDirection : "asc"}
                onClick={() => handleRequestSort("duration")}
              >
                <Stack direction="row" alignItems="center" gap={0.5}>
                  Duration
                  <Tooltip
                    title="Duration is the time from when plan generation started to when the final response was received from the automation server."
                    arrow
                    placement="top"
                  >
                    <HelpOutline fontSize="small" color="info" />
                  </Tooltip>
                </Stack>
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{
                width: COL_WIDTHS.timeInQueue,
                minWidth: COL_WIDTHS.timeInQueue,
                maxWidth: COL_WIDTHS.timeInQueue,
              }}
            >
              Time In Queue
            </TableCell>
            <TableCell
              sortDirection={sortKey === "status" ? sortDirection : false}
              sx={{
                width: COL_WIDTHS.status,
                minWidth: COL_WIDTHS.status,
                maxWidth: COL_WIDTHS.status,
              }}
            >
              <TableSortLabel
                active={sortKey === "status"}
                direction={sortKey === "status" ? sortDirection : "asc"}
                onClick={() => handleRequestSort("status")}
              >
                Status
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedRows.map((r, idx) => (
            <TableRow key={idx} hover>
              <TableCell
                sx={{
                  width: COL_WIDTHS.orderId,
                  minWidth: COL_WIDTHS.orderId,
                  maxWidth: COL_WIDTHS.orderId,
                }}
              >
                {r.orderId}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.planName,
                  minWidth: COL_WIDTHS.planName,
                  maxWidth: COL_WIDTHS.planName,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={r.planName || ""}
              >
                {`[${r.id}] `}
                {r.planName || "-"}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.template,
                  minWidth: COL_WIDTHS.template,
                  maxWidth: COL_WIDTHS.template,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.templateName || "-"}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.prescription,
                  minWidth: COL_WIDTHS.prescription,
                  maxWidth: COL_WIDTHS.prescription,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.relatedPrescriptionId || r.relatedPrescription
                  ? `${r.relatedPrescriptionId ? `[${r.relatedPrescriptionId}] ` : ""}${r.relatedPrescription || "-"}`
                  : "-"}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.started,
                  minWidth: COL_WIDTHS.started,
                  maxWidth: COL_WIDTHS.started,
                }}
              >
                {r.planningStartedAt
                  ? toLocaleDateTimeString(r.planningStartedAt)
                  : "-"}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.finished,
                  minWidth: COL_WIDTHS.finished,
                  maxWidth: COL_WIDTHS.finished,
                }}
              >
                {r.finishedAt ? toLocaleDateTimeString(r.finishedAt) : "-"}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.duration,
                  minWidth: COL_WIDTHS.duration,
                  maxWidth: COL_WIDTHS.duration,
                }}
              >
                {formatDurationRange(r.planningStartedAt, r.finishedAt) || "-"}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.timeInQueue,
                  minWidth: COL_WIDTHS.timeInQueue,
                  maxWidth: COL_WIDTHS.timeInQueue,
                }}
              >
                {!r.currentStep || r.currentStep.trim() === ""
                  ? formatDurationRange(
                      r.planningStartedAt,
                      dayjs().toISOString(),
                    ) || "-"
                  : "-"}
              </TableCell>
              <TableCell
                sx={{
                  width: COL_WIDTHS.status,
                  minWidth: COL_WIDTHS.status,
                  maxWidth: COL_WIDTHS.status,
                }}
              >
                {!r.finishedAt ? (
                  <Chip
                    size="small"
                    label="In Progress"
                    variant="filled"
                    sx={{ bgcolor: COLOUR_BLIND_COLOURS.inProgress }}
                  />
                ) : r.success ? (
                  <Chip
                    size="small"
                    label="Success"
                    variant="filled"
                    sx={{
                      bgcolor: COLOUR_BLIND_COLOURS.success,
                      color: "white",
                    }}
                  />
                ) : (
                  <Tooltip
                    title={r.error || "Unknown error"}
                    arrow
                    placement="right"
                  >
                    <Chip
                      size="small"
                      label="Error"
                      variant="filled"
                      sx={{
                        bgcolor: COLOUR_BLIND_COLOURS.error,
                        color: "white",
                      }}
                    />
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography variant="body2" color="text.secondary">
                  No data available for the selected date range.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={sortedRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </TableContainer>
  );
};

export default MetricsTable;
